/**
 * description-formatter.js
 *
 * Two responsibilities:
 * 1. Raw spec-dump descriptions → clean spec table (original behaviour)
 * 2. Structured HTML descriptions → split out "Technische Daten", "FAQ",
 *    "Lieferumfang" sections into their corresponding product-tabs panels,
 *    leaving only the intro text + feature bullets in the description panel.
 */
(function () {
  'use strict';

  /* ─────────────────────────────────────────────────────────────
     SECTION 1: raw spec-dump → table (original logic, unchanged)
     ───────────────────────────────────────────────────────────── */

  const SPEC_KEYS = [
    'Produkttyp', 'Produktart', 'Produktkategorie',
    'Allgemeines', 'Serie', 'Modellbezeichnung', 'Modellnummer', 'Modell',
    'Betriebssystem', 'Android-Version', 'OS',
    'Bildschirm', 'Displaygröße', 'Displaytyp', 'Bildschirmgröße',
    'Auflösung', 'Pixeldichte', 'Bildwiederholfrequenz',
    'Kamera', 'Hauptkamera', 'Frontkamera', 'Rückkamera', 'Kameraauflösung',
    'Speicher', 'Interner Speicher', 'Erweiterbar', 'RAM', 'Arbeitsspeicher',
    'Prozessor', 'CPU', 'GPU', 'Chipsatz', 'Chip', 'Taktfrequenz',
    'Akku', 'Akkukapazität', 'Akkulaufzeit', 'Ladezeit', 'Ladegeschwindigkeit', 'Batterie',
    'Verbindung', 'Konnektivität', 'Bluetooth', 'WLAN', 'WiFi', 'USB', 'NFC',
    'GPS', 'Mobilfunk', 'SIM',
    'Abmessungen', 'Maße', 'Gewicht', 'Größe', 'Tiefe',
    'Lieferumfang', 'Zubehör', 'Inhalt',
    'Sound', 'Audio', 'Lautsprecher', 'Mikrofon',
    'Features', 'Funktionen', 'Besonderheiten', 'Sonstiges', 'Extras',
    'Design', 'Material', 'Farbe', 'Gehäuse', 'Schutzklasse',
    'Sensoren', 'Beschleunigungssensor', 'Herzfrequenzsensor',
    'Herstellergarantie', 'Garantie',
    'Wasserbeständigkeit', 'Wasserdicht',
    'Netzteil', 'Ladegerät', 'Ladeanschluss',
    'Zertifizierungen', 'Zertifizierung',
  ];
  SPEC_KEYS.sort((a, b) => b.length - a.length);

  function escHtml(str) {
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function countKeywords(text) {
    let count = 0;
    for (const key of SPEC_KEYS) {
      if (text.includes(key)) {
        if (++count >= 4) return count;
      }
    }
    return count;
  }

  function isSpecDump(container) {
    const html = container.innerHTML;
    if (/<(ul|ol|table|dl|h[1-6])\b/i.test(html)) return false;
    if ((html.match(/<p\b/gi) || []).length >= 3) return false;
    if ((html.match(/<br/gi) || []).length >= 3) return false;
    const text = container.textContent.replace(/\s+/g, ' ').trim();
    if (text.length < 60) return false;
    return countKeywords(text) >= 4;
  }

  function parseSpec(text) {
    const norm = text.replace(/\s+/g, ' ').trim();
    const hits = [];
    for (const key of SPEC_KEYS) {
      let searchFrom = 0;
      while (searchFrom < norm.length) {
        const idx = norm.indexOf(key, searchFrom);
        if (idx === -1) break;
        const before = norm[idx - 1];
        const after  = norm[idx + key.length];
        if ((idx === 0 || before === ' ') && (!after || after === ' ')) {
          hits.push({ pos: idx, key, end: idx + key.length });
        }
        searchFrom = idx + 1;
      }
    }
    hits.sort((a, b) => a.pos - b.pos || b.key.length - a.key.length);
    const unique = [];
    let cursor = -1;
    for (const hit of hits) {
      if (hit.pos > cursor) {
        unique.push(hit);
        cursor = hit.pos;
      }
    }
    const entries = [];
    for (let i = 0; i < unique.length; i++) {
      const cur  = unique[i];
      const next = unique[i + 1];
      const valueStart = cur.end + (norm[cur.end] === ' ' ? 1 : 0);
      const valueEnd   = next ? next.pos : norm.length;
      const value = norm.slice(valueStart, valueEnd).trim();
      if (value) entries.push({ key: cur.key, value });
    }
    return entries;
  }

  function buildSpecTable(entries) {
    const table = document.createElement('table');
    table.className = 'spec-table';
    table.setAttribute('aria-label', 'Technische Daten');
    const tbody = document.createElement('tbody');
    for (const { key, value } of entries) {
      const tr = document.createElement('tr');
      tr.innerHTML =
        `<td class="spec-table__key">${escHtml(key)}</td>` +
        `<td class="spec-table__value">${escHtml(value)}</td>`;
      tbody.appendChild(tr);
    }
    table.appendChild(tbody);
    return table;
  }

  function formatSpecDumps() {
    document.querySelectorAll('[data-description-content]').forEach((container) => {
      try {
        if (!isSpecDump(container)) return;
        const rawText = container.textContent.replace(/\s+/g, ' ').trim();
        const entries = parseSpec(rawText);
        if (entries.length < 3) return;
        const firstKeyPos = rawText.indexOf(entries[0].key);
        const intro = firstKeyPos > 0 ? rawText.slice(0, firstKeyPos).trim() : '';
        container.innerHTML = '';
        container.classList.add('spec-dump-formatted');
        if (intro) {
          const p = document.createElement('p');
          p.textContent = intro;
          container.appendChild(p);
        }
        container.appendChild(buildSpecTable(entries));
        const toggle = container.closest('product-tabs, .product-tabs')
          ?.querySelector('[data-description-toggle]');
        if (toggle) toggle.hidden = true;
      } catch (err) {
        console.warn('[description-formatter] spec-dump', err);
      }
    });
  }

  /* ─────────────────────────────────────────────────────────────
     SECTION 2: structured description → split into tabs
     ───────────────────────────────────────────────────────────── */

  /* Heading texts that signal a section which should leave the description tab */
  const SECTION_RULES = [
    {
      keywords: ['technische daten', 'technische details', 'spezifikationen', 'technische angaben'],
      panelSelector: '[id*="-specs"]',
      tabSelector: '[id*="Tab-"][id*="-specs"]',
      fallbackId: 'extracted-specs',
      fallbackTabLabel: 'Technische Daten',
      tabIcon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
        <rect x="5" y="2" width="10" height="13" rx="1.5"/>
        <path d="M5 4H3.5A1.5 1.5 0 0 0 2 5.5v11A1.5 1.5 0 0 0 3.5 18h13a1.5 1.5 0 0 0 1.5-1.5V5.5A1.5 1.5 0 0 0 16.5 4H15"/>
        <line x1="7.5" y1="8" x2="12.5" y2="8"/>
        <line x1="7.5" y1="11" x2="12.5" y2="11"/>
      </svg>`,
    },
    {
      keywords: ['häufige fragen', 'häufig gestellte fragen', 'faq'],
      panelSelector: '[id*="-faq"]',
      tabSelector: '[id*="Tab-"][id*="-faq"]',
      fallbackId: 'extracted-faq',
      fallbackTabLabel: 'FAQ',
      tabIcon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="10" cy="10" r="8"/>
        <path d="M10 14v.5"/>
        <path d="M7.5 7.5a2.5 2.5 0 0 1 5 0c0 1.5-2.5 2-2.5 3.5"/>
      </svg>`,
    },
    {
      keywords: ['lieferumfang', 'im lieferumfang enthalten', 'inhalt der lieferung'],
      panelSelector: null,
      tabSelector: null,
      fallbackId: 'extracted-package',
      fallbackTabLabel: 'Lieferumfang',
      tabIcon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
        <rect x="2" y="7" width="16" height="11" rx="1.5"/>
        <path d="M7 7V5a3 3 0 0 1 6 0v2"/>
        <line x1="10" y1="11" x2="10" y2="15"/>
        <line x1="8" y1="13" x2="12" y2="13"/>
      </svg>`,
    },
  ];

  /**
   * Given a heading element, collect all following siblings until the
   * next same-or-higher-level heading.
   */
  function collectSectionNodes(headingEl) {
    const nodes = [];
    const headingLevel = parseInt(headingEl.tagName[1], 10);
    let sibling = headingEl.nextElementSibling;
    while (sibling) {
      const sibLevel = sibling.tagName && parseInt(sibling.tagName[1], 10);
      if (sibling.tagName && /^H[1-6]$/.test(sibling.tagName) && sibLevel <= headingLevel) break;
      nodes.push(sibling);
      sibling = sibling.nextElementSibling;
    }
    return nodes;
  }

  /**
   * Find or create a tab + panel for an extracted section.
   * Returns { tab, panel } or null if we can't/shouldn't create one.
   */
  function resolveTabPanel(tabsEl, rule, sectionId) {
    /* Try to find an existing panel first */
    let panel = rule.panelSelector ? tabsEl.querySelector(rule.panelSelector) : null;
    let tab   = rule.tabSelector   ? tabsEl.querySelector(rule.tabSelector)   : null;

    /* If the panel already has real content (not just whitespace), skip */
    if (panel && panel.textContent.trim().length > 20) return null;

    /* Create tab + panel dynamically */
    if (!panel) {
      const tabList = tabsEl.querySelector('[role="tablist"]');
      if (!tabList) return null;

      const panelId = `ProductPanel-extracted-${sectionId}-${rule.fallbackId}`;
      const tabId   = `ProductTab-extracted-${sectionId}-${rule.fallbackId}`;

      tab = document.createElement('button');
      tab.type = 'button';
      tab.id = tabId;
      tab.className = 'product-tabs__tab';
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-selected', 'false');
      tab.setAttribute('aria-controls', panelId);
      tab.setAttribute('tabindex', '-1');
      tab.dataset.dynamic = '1';
      tab.innerHTML = `
        <span class="product-tabs__tab-icon" aria-hidden="true">${rule.tabIcon}</span>
        <span>${rule.fallbackTabLabel}</span>
      `;
      // Insert before the first policy tab (Versand/Rückgabe) so product-info tabs stay together
      const policyTab = tabList.querySelector('[data-hash="versand"], [data-hash="rueckgabe"]');
      if (policyTab) {
        tabList.insertBefore(tab, policyTab);
      } else {
        tabList.appendChild(tab);
      }

      panel = document.createElement('div');
      panel.id = panelId;
      panel.className = 'product-tabs__panel rte';
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-labelledby', tabId);
      panel.hidden = true;
      tabsEl.appendChild(panel);
    }

    return { tab, panel };
  }

  function splitStructuredDescription() {
    document.querySelectorAll('product-tabs.product-tabs').forEach((tabsEl) => {
      const descContent = tabsEl.querySelector('[data-description-content]');
      if (!descContent) return;

      /* Only run on structured HTML (has headings) */
      const headings = Array.from(descContent.querySelectorAll('h1, h2, h3, h4'));
      if (headings.length === 0) return;

      /* Unique ID seed from the tabsEl DOM position */
      const sectionId = tabsEl.closest('[data-section]')?.dataset?.section || Math.random().toString(36).slice(2);

      headings.forEach((heading) => {
        const headingText = heading.textContent.trim().toLowerCase();

        for (const rule of SECTION_RULES) {
          if (!rule.keywords.some((kw) => headingText.includes(kw))) continue;

          const result = resolveTabPanel(tabsEl, rule, sectionId);
          if (!result) break;

          const { panel } = result;

          /* Collect body nodes for this section */
          const bodyNodes = collectSectionNodes(heading);

          /* Clone content into target panel */
          const wrapper = document.createElement('div');
          wrapper.className = 'rte';

          /* Re-use the heading as a section heading in the panel */
          const panelHeading = document.createElement('h3');
          panelHeading.className = 'product-tabs__section-heading';
          panelHeading.textContent = heading.textContent.trim();
          wrapper.appendChild(panelHeading);

          bodyNodes.forEach((node) => wrapper.appendChild(node.cloneNode(true)));
          panel.innerHTML = '';
          panel.appendChild(wrapper);

          /* Remove the heading + its body from the description panel */
          bodyNodes.forEach((node) => node.remove());
          heading.remove();

          break; /* Only match one rule per heading */
        }
      });

      /* After extraction: re-run the clamp check with accurate post-extraction height */
      const productTabsEl = tabsEl;
      if (typeof productTabsEl.syncDescriptionClamp === 'function') {
        /* Reset expanded state so sync calculates fresh */
        descContent.classList.remove('is-expanded');
        productTabsEl.syncDescriptionClamp();
      } else {
        /* Fallback: hide toggle if remaining content is short */
        const toggle = tabsEl.querySelector('[data-description-toggle]');
        const lineHeight = Number.parseFloat(window.getComputedStyle(descContent).lineHeight || '24');
        const threshold = lineHeight * 10 + 8;
        if (toggle && descContent.scrollHeight <= threshold) {
          toggle.hidden = true;
          descContent.classList.remove('is-clamped');
        }
      }
    });
  }

  /* ─────────────────────────────────────────────────────────────
     SECTION 3: integrate dynamically added tabs into product-tabs.js
     ───────────────────────────────────────────────────────────── */

  /**
   * After splitStructuredDescription() adds dynamic tabs, we need to:
   * 1. Wire click/keydown handlers for the new tabs only (static tabs
   *    are already wired by product-tabs.js connectedCallback).
   * 2. Update the custom element's internal this.tabs / this.panels
   *    so that activateTab() manages ALL tabs and panels consistently.
   */
  function refreshProductTabsComponents() {
    document.querySelectorAll('product-tabs.product-tabs').forEach((tabsEl) => {
      const allTabs   = Array.from(tabsEl.querySelectorAll('[role="tab"]'));
      const allPanels = Array.from(tabsEl.querySelectorAll('[role="tabpanel"]'));

      if (tabsEl.onClick && Array.isArray(tabsEl.tabs)) {
        /* product-tabs.js is initialised — capture original list before overwriting */
        const originalTabs = tabsEl.tabs;

        /* Wire only newly added dynamic tabs */
        allTabs.forEach((tab) => {
          if (!originalTabs.includes(tab)) {
            tab.addEventListener('click', tabsEl.onClick);
            tab.addEventListener('keydown', tabsEl.onKeydown);
          }
        });

        /* Expand internal lists so activateTab manages everything */
        tabsEl.tabs   = allTabs;
        tabsEl.panels = allPanels;
      } else {
        /* Fallback: product-tabs.js not ready — attach basic handlers */
        allTabs.forEach((tab) => {
          if (tab.dataset.dynamicWired) return;
          tab.dataset.dynamicWired = '1';
          tab.addEventListener('click', () => {
            allTabs.forEach((t) => {
              t.setAttribute('aria-selected', 'false');
              t.setAttribute('tabindex', '-1');
            });
            allPanels.forEach((p) => { p.hidden = true; });
            tab.setAttribute('aria-selected', 'true');
            tab.removeAttribute('tabindex');
            const targetPanel = tabsEl.querySelector(`#${tab.getAttribute('aria-controls')}`);
            if (targetPanel) targetPanel.hidden = false;
          });
        });
      }
    });
  }

  /* ─────────────────────────────────────────────────────────────
     Bootstrap
     ───────────────────────────────────────────────────────────── */

  function stripDescriptionArtefacts() {
    document.querySelectorAll('.product__description, .product-description, [data-product-description]').forEach((el) => {
      // Remove injected .fixlogo style blocks (plain text artefacts from scraper)
      el.querySelectorAll('style').forEach((s) => { if (s.textContent.includes('.fixlogo')) s.remove(); });
      // Strip "Keine Designvorlage" / "Produktbeschreibung" prefix from first text node
      const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
      const first = walker.nextNode();
      if (first) {
        first.textContent = first.textContent.replace(/^(Keine Designvorlage\s*|Produktbeschreibung\s*)+/i, '');
      }
      // Remove .fixlogo text if it leaked as raw text (not inside <style>)
      el.innerHTML = el.innerHTML.replace(/\s*\.fixlogo\s*\{[^}]*\}/gi, '');
    });
  }

  function run() {
    stripDescriptionArtefacts();
    splitStructuredDescription(); /* must run before spec-dump so headings are gone */
    formatSpecDumps();
    refreshProductTabsComponents();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }

  document.addEventListener('shopify:section:load', run);
})();
